/**
 * Installments container (section `installments`, C33, B4 — D07 §1.1, §2, §5; D-105 – D-109, D-117, D-291, D-293,
 * D-294): binds `Installments` / `NewContractForm` / `ContractDetailModal` / `PaymentModal` / `ReasonDialog`
 * (packages/ui, old InstallmentsView.tsx) to the read model `GET /installments` and the contract detail, and posts
 * through the D07 routes: contract preview → personal PIN (own or a supervisor's, D-018) → commit; payment preview →
 * PIN → commit (D-108); schedule edit with PIN (D-109 (3)); cancel of a contract or a payment with a reason and the
 * own PIN (D-109 (4)). The form's figures are the server's preview (`derived`, `schedule`), requested when the inputs
 * change (debounced) — nothing is computed here but labels. A new customer is opened inside the contract
 * (`newAccounts`, D-245 (e)); a trust account is resolved to the same person's debt account, opened inside the
 * contract when missing (T-12). Prints: the A4 contract p06 and the A4 list through the print root after the audit call
 * (D-147); WhatsApp texts carry the office name (D-052, R-14).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InstallmentContractA4, InstallmentListA4, PRINT_ROOT_CLASS, printDocument } from "@faylasuf/print";
import {
  ContractDetailModal, Installments, NewContractForm, PaymentModal, ReasonDialog, formatWithCommas,
  type ContractFigures, type ContractFormValues, type InstallmentsAlertRow, type InstallmentsCard, type InstallmentsStatusFilter, type InstallmentsTab, type SelectOption,
} from "@faylasuf/ui";
import type { ContractPreviewBody, ContractPreviewResult, ContractView, InstallmentsView, NewAccountSpec, PaymentPreviewResult } from "../api/types.js";
import { isApiClientError } from "../api/client.js";
import { useApp, useSession } from "../app/context.js";
import { engineErrors, errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { usePrintOffice } from "../app/printOffice.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { ReceiptWindow } from "./installments/ReceiptWindow.js";
import { daysBetween, money, officeDate, pctOf, periodWord, profitAbove, progress, roundWhole, trimZeros, whatsappUrl } from "./installments/format.js";

const IQD_LABEL = "IQD";

function emptyForm(today: string): ContractFormValues {
  return {
    customerMode: "new", customerRowId: null, name: "", phone: "", address: "",
    itemType: "product", productKey: null, cardBoxId: null, cardAmount: "", vaultSource: "dollar_vault", currencyCode: "", vaultAmount: "", fxAmount: "", itemName: "",
    currency: "IQD", basePrice: "", profitPct: "20", downPayment: "0", downPaymentAccountId: null, period: "monthly", count: "6", deductionPct: "0", firstDueDate: today, note: "",
  };
}

type PrintJob =
  | { readonly kind: "contract"; readonly contract: ContractView; readonly copy: boolean }
  | { readonly kind: "list" };

export function InstallmentsPage() {
  const { t, locale, source } = useApp();
  const { hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice();
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  const q = useQuery({ queryKey: keys.installments, queryFn: () => source.installments(), staleTime: 10_000 });
  if (q.error && isSessionLost(q.error)) sessionLost();
  const data: InstallmentsView | undefined = q.data;
  const customersQ = useQuery({ queryKey: keys.customers(false), queryFn: () => source.customers(false), enabled: hasPermission("installments_create"), retry: false });
  const accountsQ = useQuery({ queryKey: keys.accounts, queryFn: () => source.accounts(), retry: false, staleTime: 10_000 });
  const storeQ = useQuery({ queryKey: keys.store, queryFn: () => source.store(), enabled: Boolean(data?.storeOn) && hasPermission("store") && hasPermission("installments_create"), retry: false });
  const currenciesQ = useQuery({ queryKey: keys.currencies, queryFn: () => source.currencies(), enabled: hasPermission("currencies") && hasPermission("installments_create"), retry: false });

  const [tab, setTab] = useState<InstallmentsTab>("contracts");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<InstallmentsStatusFilter>("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [cancelContractId, setCancelContractId] = useState<string | null>(null);
  const [cancelPaymentId, setCancelPaymentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [printJob, setPrintJob] = useState<PrintJob | null>(null);

  const tz = data?.timeZone ?? "Asia/Baghdad";
  const today = data?.today ?? officeDate(new Date().toISOString(), tz);
  const contracts = data?.contracts ?? [];
  const invalidate = () => {
    for (const k of [keys.installments, keys.accounts, keys.dashboard, keys.store, keys.journal, keys.profits]) void qc.invalidateQueries({ queryKey: k });
    void qc.invalidateQueries({ queryKey: ["customers"] });
    void qc.invalidateQueries({ queryKey: ["installments"] });
  };

  const detailQ = useQuery({ queryKey: keys.installment(detailId ?? "-"), queryFn: () => source.installment(detailId!), enabled: detailId !== null });
  const detail = detailQ.data ?? contracts.find((c) => c.id === detailId) ?? null;

  // ---------------------------------------------------------------- list
  const visibleRow = (c: ContractView) => {
    const s = search.toLowerCase();
    const match = c.customer.name.toLowerCase().includes(s) || c.contractNumber.toLowerCase().includes(s) || c.item.name.toLowerCase().includes(s) || (c.customer.phone ?? "").includes(search);
    if (!match) return false;
    const d = officeDate(c.createdAt, tz);
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    if (status === "active") return c.status === "active" || c.status === "late";
    if (status === "completed") return c.status === "completed";
    return true;
  };
  const filtered = contracts.filter(visibleRow);
  const periodLabel = (c: ContractView) => `${periodWord(t, c.period)} (${t("in.n_payments", { n: c.installments })})`;
  const cards: InstallmentsCard[] = filtered.map((c) => ({
    id: c.id,
    contractNumber: c.contractNumber,
    customer: c.customer.name,
    phone: c.customer.phone,
    status: c.status,
    dateLabel: officeDate(c.createdAt, tz),
    itemName: c.item.name,
    totalLabel: money(c.total, c.currency),
    paidLabel: money(c.paidWithDown, c.currency),
    progressPct: progress(c.paidWithDown, c.total),
    periodLabel: periodLabel(c),
    installmentLabel: money(c.installmentAmount, c.currency),
    canCancel: Boolean(data?.can.cancel) && (c.status === "active" || c.status === "late" || c.status === "completed"),
  }));

  const reminder = (c: { customer: { name: string }; item: { name: string }; contractNumber: string }, no: number, open: string, cur: string, due: string) =>
    t("in.wa_reminder", { name: c.customer.name, n: no, amount: money(open, cur), item: c.item.name, contract: c.contractNumber, date: due, office: printOffice.name });
  const alerts: InstallmentsAlertRow[] = (data?.alerts ?? []).map((a) => {
    const k = contracts.find((c) => c.id === a.contractId);
    return {
      key: `${a.contractId}-${a.no}`,
      contractId: a.contractId,
      customer: a.customer.name,
      phone: a.customer.phone,
      itemName: a.itemName,
      contractNumber: a.contractNumber,
      rowNo: a.no,
      rowCount: k?.rows.length ?? a.no,
      dueDate: a.dueDate,
      openLabel: money(a.open, a.currency),
      today: a.due === "due_today",
      daysLate: Math.max(0, daysBetween(a.dueDate, today)),
      whatsappUrl: a.customer.phone ? whatsappUrl(a.customer.phone, reminder({ customer: a.customer, item: { name: a.itemName }, contractNumber: a.contractNumber }, a.no, a.open, a.currency, a.dueDate)) : null,
    };
  });
  const tiles = useMemo(() => {
    const m = (x: { IQD: string; USD: string }) => ({ iqd: x.IQD !== "0" ? formatWithCommas(trimZeros(x.IQD)) : null, usd: x.USD !== "0" ? formatWithCommas(trimZeros(x.USD)) : null });
    const tl = data?.tiles;
    return {
      contracts: tl?.contracts ?? 0,
      active: tl?.active ?? 0,
      withProfit: tl ? m(tl.salesWithProfit) : { iqd: null, usd: null },
      withoutProfit: tl ? m(tl.salesWithoutProfit) : { iqd: null, usd: null },
      due: tl?.dueOrOverdue ?? 0,
      veryOverdue: tl?.veryOverdue ?? 0,
    };
  }, [data]);

  // ---------------------------------------------------------------- pickers
  const accounts = accountsQ.data ?? [];
  const customerRows = customersQ.data?.rows ?? [];
  const [values, setValues] = useState<ContractFormValues>(() => emptyForm(today));
  const cur = values.currency;
  const iqdVault = accounts.find((a) => a.kind === "cash_vault" && a.currency === "IQD" && !a.archived);
  const usdVault = accounts.find((a) => a.kind === "cash_vault" && a.currency === "USD" && !a.archived);
  const cashOptions = (currency: string): SelectOption[] =>
    accounts
      .filter((a) => !a.archived && a.currency === currency && (a.kind === "cash_vault" || a.kind === "cash_box" || a.kind === "digital_box"))
      .map((a) => ({ id: a.id, label: a.name ?? a.kind, subLabel: currency === "IQD" ? `(د.ع ${formatWithCommas(trimZeros(a.balance))})` : `($ ${formatWithCommas(trimZeros(a.balance))})` }));
  const customerOptions: SelectOption[] = customerRows.map((r) => ({
    id: r.id,
    label: `${r.name}${r.phone ? ` (${r.phone})` : ""}${r.kind === "trust" ? ` — ${t("in.trust_account")}` : ""}`,
    subLabel: `(د.ع ${formatWithCommas(trimZeros(r.iqd.balance))})`,
  }));
  const store = storeQ.data;
  const productOptions = (store?.products ?? [])
    .filter((p) => !p.archived)
    .flatMap((p) =>
      p.stock
        .filter((s) => s.quantity !== "0")
        .map((s) => ({
          key: `${p.id}:${s.warehouseId}`,
          name: p.name,
          warehouseName: store?.warehouses.find((w) => w.id === s.warehouseId)?.name ?? "",
          priceLabel: `(د.ع ${formatWithCommas(trimZeros(p.salePrice))})`,
          stockLabel: t("in.stock_badge", { n: trimZeros(s.quantity) }),
          costPrice: s.averageCost ? roundWhole(s.averageCost) : null,
          salePrice: p.salePrice !== "0" ? roundWhole(p.salePrice) : null,
        })),
    );
  const fxList = (currenciesQ.data?.currencies ?? []).filter((c) => c.visible && !c.suspended && c.stock && c.rates);
  const currencyOptions = fxList.map((c) => ({
    code: c.code,
    nameAr: locale === "ar" ? c.nameAr : c.nameEn,
    symbol: c.symbol,
    balanceLabel: formatWithCommas(trimZeros(c.stock!.balance)),
    rateLabel: `${c.quoteUnit === "1" ? "1" : formatWithCommas(c.quoteUnit)} ${c.symbol} = ${formatWithCommas(trimZeros(c.rates!.sell))} د.ع`,
  }));

  // ---------------------------------------------------------------- contract preview (debounced)
  const [preview, setPreview] = useState<ContractPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [duplicate, setDuplicate] = useState<{ name: string; rowId: string } | null>(null);
  const [debtFor, setDebtFor] = useState<string | null>(null); // T-12: the person whose debt account is opened inside
  const [fxBase, setFxBase] = useState<string | null>(null); // D-293: the server's quantity × sell rate
  const seq = useRef(0);

  useEffect(() => {
    setDebtFor(null);
  }, [values.customerRowId, values.customerMode, values.currency]);
  useEffect(() => {
    setFxBase(null);
  }, [values.itemType, values.vaultSource, values.currencyCode, values.fxAmount]);

  const buildBody = (v: ContractFormValues): ContractPreviewBody | null => {
    const count = Number(v.count || "0");
    if (!count || !v.firstDueDate || !v.itemName.trim()) return null;
    let customerAccountId: string;
    let newAccounts: NewAccountSpec[] | undefined;
    if (v.customerMode === "existing") {
      const row = customerRows.find((r) => r.id === v.customerRowId);
      if (!row) return null;
      if (debtFor) {
        customerAccountId = "new:d";
        newAccounts = [{ ref: "d", kind: "debt", currency: v.currency, personId: debtFor }];
      } else customerAccountId = v.currency === "USD" ? row.usd.accountId : row.iqd.accountId;
    } else {
      if (!v.name.trim()) return null;
      customerAccountId = "new:c";
      newAccounts = [{ ref: "c", kind: "debt", currency: v.currency, name: v.name.trim(), ...(v.phone.trim() ? { phone: v.phone.trim() } : {}), ...(v.address.trim() ? { address: v.address.trim() } : {}) }];
    }
    let item: ContractPreviewBody["item"];
    let basePrice = v.basePrice;
    if (v.itemType === "product") {
      if (v.productKey) {
        const [productId = "", warehouseId = ""] = v.productKey.split(":");
        item = { kind: "product", productId, warehouseId, quantity: "1" };
      } else item = { kind: "none" };
    } else if (v.itemType === "crypto") {
      if (!v.cardBoxId) return null;
      item = { kind: "card", boxAccountId: v.cardBoxId };
    } else if (v.vaultSource === "dollar_vault") {
      if (!usdVault || !v.vaultAmount) return null;
      item = { kind: "currency", stockAccountId: usdVault.id, quantity: v.vaultAmount };
      basePrice = v.vaultAmount;
    } else {
      const fx = fxList.find((c) => c.code === v.currencyCode);
      if (!fx || !v.fxAmount) return null;
      item = { kind: "currency", stockAccountId: fx.stock!.accountId, quantity: v.fxAmount, sellRate: fx.rates!.sell };
      basePrice = fxBase ?? "1";
    }
    if (!basePrice || Number(basePrice) <= 0) return null;
    return {
      customerAccountId,
      itemName: v.itemName.trim(),
      item,
      basePrice,
      profitPct: v.profitPct || "0",
      deductionPct: v.deductionPct || "0",
      downPayment: v.downPayment || "0",
      ...(Number(v.downPayment || "0") > 0 && v.downPaymentAccountId ? { downPaymentAccountId: v.downPaymentAccountId } : {}),
      period: v.period,
      installments: count,
      firstDueDate: v.firstDueDate,
      ...(v.note.trim() ? { note: v.note.trim() } : {}),
      ...(newAccounts ? { newAccounts } : {}),
    };
  };

  /** One preview; resolves the T-12 / D-293 / same-name answers of the server. */
  const runPreview = async (v: ContractFormValues): Promise<ContractPreviewResult | null> => {
    const body = buildBody(v);
    if (!body) return null;
    try {
      const r = await source.contractPreview(body);
      setDuplicate(null);
      setPreviewError(null);
      return r;
    } catch (e) {
      if (isSessionLost(e)) {
        sessionLost();
        return null;
      }
      if (isApiClientError(e) && e.code === "CUSTOMER_NAME_EXISTS") {
        const m = (e.params["matches"] as Array<{ id: string; name: string }> | undefined)?.[0];
        if (m) setDuplicate({ name: m.name, rowId: m.id });
        setPreviewError(null);
        return null;
      }
      const errs = engineErrors(e);
      const debt = errs.find((x) => x.code === "DEBT_ACCOUNT_REQUIRED");
      if (debt && typeof debt.params["owner"] === "string") {
        setDebtFor(String(debt.params["owner"]));
        return null;
      }
      const base = errs.find((x) => x.code === "AMOUNT_NOT_POSITIVE" && x.params["reason"] === "base_is_quantity_times_rate");
      if (base && typeof base.params["expected"] === "string") {
        setFxBase(String(base.params["expected"]));
        return null;
      }
      setPreviewError(errorMessage(t, e));
      return null;
    }
  };

  useEffect(() => {
    if (tab !== "new") return;
    const my = ++seq.current;
    const h = setTimeout(() => {
      // [C33 browser run] a down payment without its box is incomplete, not an error: no preview until the box is chosen
      // (the server would answer "account unknown" for the missing box)
      if (!buildBody(values) || (Number(values.downPayment || "0") > 0 && !values.downPaymentAccountId)) {
        setPreview(null);
        setPreviewError(null);
        return;
      }
      setPreviewing(true);
      void runPreview(values).then((r) => {
        if (my !== seq.current) return;
        setPreviewing(false);
        setPreview(r);
      });
    }, 600);
    return () => clearTimeout(h);
  }, [values, tab, debtFor, fxBase, customersQ.data, accountsQ.data, currenciesQ.data]);

  const figures: ContractFigures | null = useMemo(() => {
    if (!preview) return null;
    const d = preview.derived;
    const isProduct = values.itemType === "product" && values.productKey !== null;
    const profit = d["profit"] ?? "0";
    const pct = d["percentage"] ?? "0";
    const cost = d["cost"] ?? "0";
    const vsCost = isProduct ? pctOf(profit, cost) : "";
    return {
      totalLabel: money(d["total"], cur),
      percentageLabel: money(pct, cur),
      additionalLabel: isProduct ? money(profitAbove(profit, pct), cur) : null,
      profitLabel: money(profit, cur),
      costLabel: isProduct ? money(cost, cur) : null,
      profitVsCostLabel: isProduct && vsCost ? `${vsCost}% (+${money(profit, cur)})` : null,
      remainingLabel: money(d["remaining"], cur),
      installmentLabel: money(preview.schedule[0]?.amount ?? "0", cur),
      netProfitLabel: money(d["net_profit"], cur),
      netNegative: (d["net_profit"] ?? "0").startsWith("-"),
      deductionLabel: money(d["deduction"], cur),
      schedule: preview.schedule.map((s) => ({ no: s.no, dueDate: s.dueDate, amountLabel: money(s.amount, cur) })),
    };
  }, [preview, values.itemType, values.productKey, cur]);

  const submitContract = async () => {
    setBusy(true);
    try {
      const fresh = await runPreview(values);
      if (!fresh) return;
      const res = await pin.withPin((proof) => source.contractCommit(fresh.previewId, crypto.randomUUID(), proof), { message: t("in.pin_contract", { total: money(fresh.derived["total"], cur) }), allowSupervisor: true });
      if (!res) return;
      toast(res.status === "pending" ? t("in.toast_contract_pending") : t("in.toast_contract_saved", { n: res.contractNumber }), "success");
      setValues(emptyForm(today));
      setPreview(null);
      setTab("contracts");
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- payment
  const [pay, setPay] = useState<{ contract: ContractView; rowNo: number; rowAmount: string; amount: string; boxId: string | null; note: string } | null>(null);
  const [payPreview, setPayPreview] = useState<PaymentPreviewResult | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const openPay = (contract: ContractView, rowNo: number, rowAmount: string, open: string) => {
    const box = contract.currency === "USD" ? usdVault?.id : iqdVault?.id;
    setPayPreview(null);
    setPayError(null);
    setPay({ contract, rowNo, rowAmount, amount: trimZeros(open), boxId: box ?? null, note: "" });
  };
  useEffect(() => {
    if (!pay || !pay.amount || !pay.boxId || Number(pay.amount) <= 0) {
      setPayPreview(null);
      return;
    }
    let live = true;
    const h = setTimeout(() => {
      void source
        .paymentPreview({ contractId: pay.contract.id, amount: pay.amount, boxAccountId: pay.boxId!, ...(pay.note.trim() ? { note: pay.note.trim() } : {}) })
        .then((r) => {
          if (!live) return;
          setPayPreview(r);
          setPayError(null);
        })
        .catch((e: unknown) => {
          if (!live) return;
          if (isSessionLost(e)) sessionLost();
          setPayPreview(null);
          setPayError(errorMessage(t, e));
        });
    }, 400);
    return () => {
      live = false;
      clearTimeout(h);
    };
  }, [pay?.amount, pay?.boxId, pay?.note, pay?.contract.id]);
  const confirmPay = async () => {
    if (!pay || !payPreview) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.paymentCommit(payPreview.previewId, crypto.randomUUID(), proof), { message: t("in.pin_payment", { amount: money(payPreview.derived["amount"], pay.contract.currency), name: pay.contract.customer.name }), allowSupervisor: true });
      if (!res) return;
      toast(res.status === "pending" ? t("in.toast_payment_pending") : t("in.toast_payment_saved"), "success");
      setPay(null);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- schedule edit, cancels
  const editSchedule = async (body: { rowNo?: number; amount?: string; redistribute?: boolean }) => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.scheduleEdit({ contractId: detail.id, ...body }, proof), { message: t("in.pin_schedule", { n: detail.contractNumber }), allowSupervisor: true });
      if (!res) return;
      toast(t("in.toast_schedule_saved"), "success");
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const cancelContract = contracts.find((c) => c.id === cancelContractId) ?? null;
  const confirmCancelContract = async (reason: string) => {
    if (!cancelContract) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.contractCancel(cancelContract.id, reason, crypto.randomUUID(), proof.pin ?? ""), { message: t("in.pin_cancel_contract", { n: cancelContract.contractNumber }) });
      if (!res) return;
      toast(t("in.toast_contract_cancelled"), "success");
      setCancelContractId(null);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const confirmCancelPayment = async (reason: string) => {
    if (!cancelPaymentId) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.paymentCancel(cancelPaymentId, reason, crypto.randomUUID(), proof.pin ?? ""), { message: t("in.pin_cancel_payment") });
      if (!res) return;
      toast(t("in.toast_payment_cancelled"), "success");
      setCancelPaymentId(null);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- prints and shares (D-147)
  useEffect(() => {
    if (!printJob) return;
    const done = () => setPrintJob(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument(printJob.kind === "contract" ? "installment-print-area" : "contracts-list-print-area"), 120);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printJob]);
  const printContract = async (id: string, channel: "print" | "pdf") => {
    try {
      const full = await source.installment(id);
      const r = await source.contractPrinted(id, channel);
      setPrintJob({ kind: "contract", contract: full, copy: r.copy });
    } catch (e) {
      fail(e);
    }
  };
  const shareContract = async (id: string) => {
    try {
      const c = await source.installment(id);
      await source.contractPrinted(id, "whatsapp");
      const lines = c.rows
        .map((r) => t("in.wa_row", { n: r.no, amount: money(r.amount, c.currency), date: r.dueDate, status: r.status === "paid" ? t("in.wa_paid") : r.status === "partial" ? t("in.wa_partial") : t("in.wa_due") }))
        .join("\n");
      const text = t("in.wa_contract", {
        name: c.customer.name, office: printOffice.name, contract: c.contractNumber, item: c.item.name, total: money(c.total, c.currency), paid: money(c.paidWithDown, c.currency),
        remaining: money(c.remaining, c.currency), installment: money(c.installmentAmount, c.currency), period: periodWord(t, c.period), rows: lines,
      });
      window.open(whatsappUrl(c.customer.phone, text), "_blank", "noopener");
    } catch (e) {
      fail(e);
    }
  };
  const shareList = () => {
    const lines = filtered
      .map((c, i) => t("in.wa_list_row", { i: i + 1, name: c.customer.name, contract: c.contractNumber, item: c.item.name, total: money(c.total, c.currency), paid: money(c.paidWithDown, c.currency), remaining: money(c.remaining, c.currency), status: c.status === "completed" ? t("in.wa_completed") : t("in.wa_active") }))
      .join("\n------------------------------------\n");
    const text = t("in.wa_list", { office: printOffice.name, date: today, n: filtered.length, filter: statusName(status), rows: lines || t("in.wa_list_empty") });
    window.open(whatsappUrl(null, text), "_blank", "noopener");
  };
  const statusName = (s: InstallmentsStatusFilter) => (s === "all" ? t("in.all") : s === "active" ? t("in.active_word") : t("in.completed_word"));

  // ---------------------------------------------------------------- render
  if (!data) return <div className="p-6 text-center text-xs font-bold text-gray-400" data-testid="installments-loading">{q.error ? errorMessage(t, q.error) : t("in.loading")}</div>;

  const cardBox = accounts.find((a) => a.id === values.cardBoxId);
  const newForm = (
    <NewContractForm
      values={values}
      onChange={(patch) => setValues((v) => ({ ...v, ...patch, ...(patch.currency && patch.currency !== v.currency ? { downPaymentAccountId: null } : {}) }))}
      customers={customerOptions}
      onSelectCustomer={(rowId) => {
        const r = customerRows.find((x) => x.id === rowId);
        if (r) setValues((v) => ({ ...v, customerMode: "existing", customerRowId: r.id, name: r.name, phone: r.phone ?? "", address: r.address ?? "" }));
      }}
      duplicate={duplicate}
      onUseDuplicate={() => {
        const r = customerRows.find((x) => x.id === duplicate?.rowId);
        setDuplicate(null);
        if (r) setValues((v) => ({ ...v, customerMode: "existing", customerRowId: r.id, name: r.name, phone: r.phone ?? "", address: r.address ?? "" }));
      }}
      products={productOptions}
      storeOn={data.storeOn && hasPermission("store")}
      cardBoxes={cashOptions(IQD_LABEL).filter((o) => accounts.find((a) => a.id === o.id)?.kind === "digital_box")}
      cardBoxInfo={cardBox ? { name: cardBox.name ?? "", iqdLabel: `${formatWithCommas(trimZeros(cardBox.balance))} د.ع` } : null}
      dollarVault={usdVault ? { name: usdVault.name ?? "", balanceLabel: formatWithCommas(trimZeros(usdVault.balance)) } : null}
      currencies={currencyOptions}
      downPaymentBoxes={cashOptions(cur)}
      figures={figures}
      previewError={previewError}
      previewing={previewing}
      busy={busy}
      onSubmit={() => void submitContract()}
      onCancel={() => {
        setValues(emptyForm(today));
        setTab("contracts");
      }}
    />
  );

  const detailRows = detail
    ? detail.rows.map((r) => ({
        no: r.no,
        dueDate: r.dueDate,
        amount: trimZeros(r.amount),
        amountLabel: money(r.amount, detail.currency),
        status: r.status,
        overdue: r.status !== "paid" && (r.due === "overdue" || r.due === "very_overdue"),
        paidLabel: formatWithCommas(trimZeros(r.paid)),
        openLabel: money(r.open, detail.currency),
        open: r.open,
      }))
    : [];
  const live = detail ? detail.status === "active" || detail.status === "late" : false;
  const statusNote = detail
    ? detail.status === "cancelled"
      ? t("in.note_cancelled", { reason: detail.cancel?.reason ?? "" })
      : detail.status === "pending"
        ? t("in.note_pending")
        : detail.status === "rejected"
          ? t("in.note_rejected", { reason: detail.rejectReason ?? "" })
          : detail.status === "withdrawn"
            ? t("in.note_withdrawn")
            : null
    : null;

  return (
    <>
      <Installments
        tab={tab}
        onTab={setTab}
        tiles={tiles}
        contractsCount={contracts.length}
        canCreate={data.can.create}
        canCollect={data.can.collect}
        newForm={newForm}
        alerts={alerts}
        onPayAlert={(a) => {
          const c = contracts.find((x) => x.id === a.contractId);
          const row = c?.rows.find((r) => r.no === a.rowNo);
          if (c && row) openPay(c, row.no, row.amount, row.open);
        }}
        onView={(id) => setDetailId(id)}
        search={search}
        onSearch={setSearch}
        status={status}
        onStatus={setStatus}
        counts={{ all: contracts.length, active: contracts.filter((c) => c.status === "active" || c.status === "late").length, completed: contracts.filter((c) => c.status === "completed").length }}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        cards={cards}
        onPrintList={() => setPrintJob({ kind: "list" })}
        onPdfList={() => setPrintJob({ kind: "list" })}
        onShareList={shareList}
        onPrint={(id) => void printContract(id, "print")}
        onPdf={(id) => void printContract(id, "pdf")}
        onShare={(id) => void shareContract(id)}
        onCancel={(id) => setCancelContractId(id)}
      >
        {detail && detailId && (
          <ContractDetailModal
            contractNumber={detail.contractNumber}
            customer={detail.customer.name}
            itemName={detail.item.name}
            totalLabel={money(detail.total, detail.currency)}
            paidLabel={money(detail.paidWithDown, detail.currency)}
            remainingLabel={money(detail.remaining, detail.currency)}
            statusNote={statusNote}
            rows={detailRows}
            payments={detail.payments.map((x) => ({ operationId: x.operationId, amountLabel: money(x.amount, detail.currency), dateLabel: x.officeDate, boxName: x.boxName ?? "", operator: x.operator, cancelled: x.cancelled, receiptNumber: x.receiptNumber }))}
            pendingNote={detail.pendingPayments.length > 0 ? t("in.payment_pending_note", { amount: money(detail.pendingPayments[0]!.amount, detail.currency), maker: detail.pendingPayments[0]!.maker }) : null}
            canEdit={live && data.can.editSchedule}
            canCollect={live && data.can.collect && detail.pendingPayments.length === 0}
            canCancel={data.can.cancel && (live || detail.status === "completed")}
            busy={busy}
            onSaveRow={(no, amount) => void editSchedule({ rowNo: no, amount })}
            onRedistribute={() => void editSchedule({ redistribute: true })}
            onPay={(r) => openPay(detail, r.no, r.amount, r.open)}
            onCancelContract={() => setCancelContractId(detail.id)}
            onCancelPayment={(id) => setCancelPaymentId(id)}
            onPrintReceipt={(opId) => setReceiptFor(opId)} // [C35] V-79: the receipt window
            onPrint={() => void printContract(detail.id, "print")}
            onPdf={() => void printContract(detail.id, "pdf")}
            onShare={() => void shareContract(detail.id)}
            onClose={() => setDetailId(null)}
          />
        )}
        {receiptFor && detail && <ReceiptWindow operationId={receiptFor} phone={detail.customer.phone} onClose={() => setReceiptFor(null)} />}
        {pay && (
          <PaymentModal
            customer={pay.contract.customer.name}
            rowNo={pay.rowNo}
            rowAmountLabel={money(pay.rowAmount, pay.contract.currency)}
            amount={pay.amount}
            onAmount={(v) => setPay((x) => (x ? { ...x, amount: v } : x))}
            boxes={cashOptions(pay.contract.currency)}
            boxId={pay.boxId}
            onBox={(id) => setPay((x) => (x ? { ...x, boxId: id } : x))}
            note={pay.note}
            onNote={(v) => setPay((x) => (x ? { ...x, note: v } : x))}
            previewInfo={payPreview ? { remainingAfterLabel: money(payPreview.derived["remaining_after"], pay.contract.currency), completed: payPreview.derived["completed"] === "1" } : null}
            previewError={payError}
            busy={busy}
            onConfirm={() => void confirmPay()}
            onClose={() => setPay(null)}
          />
        )}
        {cancelContract && (
          <ReasonDialog
            title={t("in.cancel_contract_title_dialog")}
            warning={t("in.cancel_contract_warning", { n: cancelContract.contractNumber, name: cancelContract.customer.name })}
            confirmLabel={t("in.cancel_confirm")}
            busy={busy}
            onConfirm={(r) => void confirmCancelContract(r)}
            onClose={() => setCancelContractId(null)}
          />
        )}
        {cancelPaymentId && (
          <ReasonDialog title={t("in.cancel_payment_title")} warning={t("in.cancel_payment_warning")} confirmLabel={t("in.cancel_confirm")} busy={busy} onConfirm={(r) => void confirmCancelPayment(r)} onClose={() => setCancelPaymentId(null)} />
        )}
      </Installments>
      <div className="relative z-[130]">{pin.dialog}</div>
      {printJob &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            {printJob.kind === "contract" ? (
              <InstallmentContractA4
                locale={locale}
                office={printOffice}
                contractNumber={printJob.contract.contractNumber}
                dateLabel={officeDate(printJob.contract.createdAt, tz)}
                copy={printJob.copy}
                cancelled={printJob.contract.status === "cancelled"}
                customerName={printJob.contract.customer.name}
                phone={printJob.contract.customer.phone}
                address={printJob.contract.customer.address}
                itemName={printJob.contract.item.name}
                totalLabel={money(printJob.contract.total, printJob.contract.currency)}
                downPaymentLabel={money(printJob.contract.downPayment, printJob.contract.currency)}
                paidLabel={money(printJob.contract.paidWithDown, printJob.contract.currency)}
                remainingLabel={money(printJob.contract.remaining, printJob.contract.currency)}
                periodLabel={periodWord(t, printJob.contract.period)}
                installmentLabel={money(printJob.contract.installmentAmount, printJob.contract.currency)}
                rows={printJob.contract.rows.map((r) => ({ no: r.no, dueDate: r.dueDate, amountLabel: money(r.amount, printJob.contract.currency), paidLabel: r.paid === "0" ? "0" : money(r.paid, printJob.contract.currency), status: r.status }))}
              />
            ) : (
              <InstallmentListA4
                locale={locale}
                office={printOffice}
                dateLabel={today}
                filterLabel={statusName(status)}
                fromDate={fromDate || null}
                toDate={toDate || null}
                rows={filtered.map((c) => ({
                  id: c.id,
                  contractNumber: c.contractNumber,
                  customerName: c.customer.name,
                  itemName: c.item.name,
                  totalLabel: money(c.total, c.currency),
                  paidLabel: money(c.paidWithDown, c.currency),
                  remainingLabel: money(c.remaining, c.currency),
                  status: c.status === "completed" ? "completed" : c.status === "cancelled" ? "cancelled" : "active",
                }))}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
